import React, { useEffect, useState } from "react";
import { Star, Send, Loader2, MessageCircle, Camera, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, uploadFile, mediaUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import Reveal from "@/components/site/Reveal";

function Stars({ value, onChange, size = "w-4 h-4" }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onChange} onClick={() => onChange && onChange(n)} onMouseEnter={() => onChange && setHover(n)}
          aria-label={`${n} estrellas`} data-testid={onChange ? `review-star-${n}` : undefined}
          className={onChange ? "cursor-pointer transition-transform hover:scale-110" : "cursor-default"}>
          <Star className={`${size} ${(hover || value) >= n ? "fill-[var(--accent-c)] text-[var(--accent-c)]" : "text-gray-300"}`} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews({ tourId = null, title, subtitle, compact = false }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", text: "", rating: 5 });
  const [photos, setPhotos] = useState([]); // {id, preview, url, progress}
  const [lightbox, setLightbox] = useState(null);
  const fileRef = React.useRef(null);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - photos.length);
    if (fileRef.current) fileRef.current.value = "";
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error("Solo se permiten imágenes"); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} supera 10MB`); continue; }
      const id = `${Date.now()}-${Math.random()}`;
      setPhotos((p) => [...p, { id, preview: URL.createObjectURL(f), url: null, progress: 0 }]);
      try {
        const url = await uploadFile(f, (pr) => setPhotos((p) => p.map((x) => (x.id === id ? { ...x, progress: pr } : x))), "review");
        setPhotos((p) => p.map((x) => (x.id === id ? { ...x, url, progress: 100 } : x)));
      } catch (err) {
        toast.error(errMsg(err, "No se pudo subir la foto"));
        setPhotos((p) => p.filter((x) => x.id !== id));
      }
    }
  };
  const uploading = photos.some((p) => !p.url);
  const [sending, setSending] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get("/reviews", { params: tourId ? { tour_id: tourId } : {} }).then((r) => setList(r.data)).catch(() => {});
  }, [tourId]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/reviews", { ...form, tour_id: tourId, photos: photos.map((p) => p.url).filter(Boolean) });
      setList((l) => [data, ...l]);
      setForm({ name: "", text: "", rating: 5 });
      setPhotos([]);
      toast.success("¡Gracias por tu comentario!");
    } catch (err) {
      toast.error(errMsg(err, "No se pudo publicar el comentario"));
    } finally { setSending(false); }
  };

  const avg = list.length ? list.reduce((s, r) => s + r.rating, 0) / list.length : 0;
  const shown = showAll ? list : list.slice(0, compact ? 4 : 6);
  const inputCls = "w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 transition-[border-color,box-shadow]";

  const formEl = (
    <form onSubmit={submit} className="bg-white rounded-xl shadow-lg p-6 h-fit" data-testid="review-form">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2"><MessageCircle className="w-5 h-5 brand-text" />Deja tu comentario</h3>
      <div className="mt-4"><label className="text-sm text-gray-600">Tu calificación</label><div className="mt-1"><Stars value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} size="w-6 h-6" /></div></div>
      <input required maxLength={80} placeholder="Tu nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} h-11 mt-4`} data-testid="review-name-input" />
      <textarea required minLength={3} maxLength={1000} rows={4} placeholder="Cuéntanos tu experiencia..." value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className={`${inputCls} py-2 mt-3 resize-none`} data-testid="review-text-input" />
      <div className="mt-3">
        <div className="flex flex-wrap gap-2">
          {photos.map((p) => (
            <div key={p.id} className="relative w-16 h-16 rounded-md overflow-hidden border border-gray-200" data-testid="review-photo-preview">
              <img src={p.preview} alt="" className="w-full h-full object-cover" />
              {!p.url && <div className="absolute inset-0 bg-black/50 text-white text-[11px] font-semibold flex items-center justify-center">{p.progress}%</div>}
              {p.url && <button type="button" onClick={() => setPhotos((x) => x.filter((y) => y.id !== p.id))} aria-label="Quitar foto"
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>}
            </div>
          ))}
          {photos.length < 4 && (
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="review-add-photo-btn"
              className="w-16 h-16 rounded-md border-2 border-dashed border-gray-300 text-gray-500 hover:border-[var(--brand)] hover:text-[var(--brand)] flex flex-col items-center justify-center text-[10px] gap-0.5 transition-colors">
              <Camera className="w-5 h-5" />Foto
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1.5">Agrega hasta 4 fotos de tu excursión (opcional)</p>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="review-photo-input" />
      </div>
      <button disabled={sending || uploading} className="btn-accent w-full h-11 rounded-md font-semibold mt-4 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="review-submit-btn">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Publicar comentario
      </button>
    </form>
  );

  const listEl = (
    <div>
      {list.length > 0 && (
        <div className={`flex items-center gap-3 mb-5 ${compact ? "text-gray-900" : "text-white"}`} data-testid="reviews-average">
          <span className="text-4xl font-bold">{avg.toFixed(1)}</span>
          <div><Stars value={Math.round(avg)} /><div className={`text-sm ${compact ? "text-gray-500" : "text-white/85"}`}>{list.length} comentario{list.length !== 1 ? "s" : ""}</div></div>
        </div>
      )}
      {list.length === 0 && <p className={`${compact ? "text-gray-500" : "text-white/90"} mb-4`}>Sé el primero en dejar un comentario.</p>}
      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        {shown.map((r) => (
          <div key={r.id} className="fade-up bg-white rounded-xl shadow-md p-5 flex gap-4" data-testid="review-card">
            <div className="w-10 h-10 shrink-0 rounded-full bg-[var(--brand)]/15 brand-text font-bold flex items-center justify-center uppercase">{r.name?.[0]}</div>
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 text-sm">{r.name}</div>
              <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5"><Stars value={r.rating} size="w-3 h-3" />{r.date}</div>
              {r.tour_title && !tourId && <div className="text-xs brand-text mt-1">{r.tour_title}</div>}
              <p className="text-sm text-gray-700 mt-2 break-words">{r.text}</p>
              {r.photos?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {r.photos.map((ph, k) => (
                    <button key={k} type="button" onClick={() => setLightbox(mediaUrl(ph))} className="w-16 h-16 sm:w-20 sm:h-20 rounded-md overflow-hidden group" data-testid="review-photo-thumb">
                      <img src={mediaUrl(ph)} alt={`Foto de ${r.name}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black border-none">
          <DialogTitle className="sr-only">Foto del comentario</DialogTitle>
          {lightbox && <img src={lightbox} alt="" className="w-full max-h-[80vh] object-contain rounded" data-testid="review-lightbox-img" />}
        </DialogContent>
      </Dialog>
      {list.length > shown.length && (
        <button onClick={() => setShowAll(true)} className={`mt-5 text-sm font-semibold underline underline-offset-4 ${compact ? "brand-text" : "text-white"}`} data-testid="reviews-show-all">Ver todos los comentarios</button>
      )}
    </div>
  );

  if (compact) {
    return (
      <div className="bg-white rounded-xl p-6 sm:p-8 mt-6 shadow-sm" data-testid="tour-reviews">
        <h2 className="text-xl font-semibold text-gray-900 mb-5">Comentarios</h2>
        <div className="grid lg:grid-cols-2 gap-6">{listEl}<div className="[&>form]:shadow-none [&>form]:border [&>form]:border-gray-100">{formEl}</div></div>
      </div>
    );
  }

  return (
    <section id="comentarios" className="py-16 sm:py-20" style={{ backgroundColor: "var(--secondary)" }} data-testid="reviews-section">
      <div className="max-w-[1200px] mx-auto px-6">
        <Reveal className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">{title}</h2>
          <p className="text-white/90 text-lg mt-3">{subtitle}</p>
        </Reveal>
        <div className="grid lg:grid-cols-[1fr_380px] gap-8">{listEl}{formEl}</div>
      </div>
    </section>
  );
}
